import {
  Popover,
  PopoverBackdrop,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { FC, Fragment } from "react";
import cn from "classnames";
import { getDataLinkAttributes } from "@/helpers";
import Link from "@/components/Link";
import Image from "next/image";
import { GlobalNavigationProps } from "../GlobalNavigation";
import { CheckMarkThin, Globe } from "@/icons";

export type LanguagesProps = {
  languageSwitcher?: Pick<
    GlobalNavigationProps,
    "superHeader"
  >["superHeader"]["languageSwitcher"];
  closeMenu: () => void;
  topOffset?: number;
  locale: string;
  asPath: string;
};

const LangDropdown: FC<LanguagesProps> = ({
  languageSwitcher,
  closeMenu,
  topOffset = 76,
  locale,
  asPath,
}) => {
  const currentLanguage = languageSwitcher?.languages?.find(
    (lang) => lang.languageCode === locale,
  ) || {
    languageCode: "en-us",
    ariaLabel: "English",
  };

  return (
    <Popover className="relative">
      <PopoverButton
        className="flex items-center"
        {...getDataLinkAttributes({
          dld: languageSwitcher?.languageButtonDLD,
          dlt: languageSwitcher?.languageButtonDLT,
        })}
      >
        <Globe className="size-5" />
      </PopoverButton>
      <PopoverBackdrop
        className="bg-swdc-black/40 fixed inset-0 z-40"
        style={{
          top: `${topOffset}px`,
        }}
      />
      <Transition
        as={Fragment}
        enter="transition-all duration-1000 ease-out"
        enterFrom="max-h-0"
        enterTo="max-h-full"
        leave="transition-all duration-200 ease-in"
        leaveFrom="max-h-full"
        leaveTo="max-h-0"
      >
        <PopoverPanel
          as="ul"
          className="text-swdc-black-600 fixed left-0 z-50 mt-7 w-full overflow-hidden rounded-b-xl bg-white shadow-[0_20px_40px_0_rgba(0,0,0,0.2)]"
        >
          <li className="border-swdc-gray-100 text-subhead-s border-b-1 border-solid py-2 pr-4 pl-6 leading-7 font-bold">
            Select a Language
          </li>
          {languageSwitcher?.languages?.map((language, i) => (
            <li
              key={`${language.title} - ${i}`}
              className={cn(
                "hover:bg-swdc-gray-50 relative flex w-full cursor-pointer bg-[#fbfbfb] last:rounded-b-xl",
                language === currentLanguage &&
                  "bg-swdc-silver-200 text-swdc-black-600 font-bold",
              )}
              onClick={() => closeMenu()}
            >
              <Link
                aria-label={language.ariaLabel}
                href={`${language.languageCode === "en-us" ? "" : `/${language.href}`}${asPath}`}
                locale={language.languageCode}
                className="text-subhead-s flex w-full items-center space-x-2 py-3 pr-11 pl-6 leading-7 font-bold"
                dld={language.dld}
                dlt={language.dlt}
              >
                {language?.icon && (
                  <Image
                    src={language.icon}
                    alt={language.ariaLabel + " icon"}
                    height={20}
                    width={20}
                    className="max-w-none"
                  />
                )}
                <span lang={language.languageCode}>{language.title}</span>
                {language === currentLanguage && (
                  <CheckMarkThin className="absolute top-1/2 right-4 w-6 -translate-y-1/2 lg:w-6" />
                )}
              </Link>
            </li>
          ))}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};

export default LangDropdown;
